import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@config/queryClient";
import useSystemStore from "@store/useSystemStore";
import useMeetingStore from "@store/useMeetingStore";
import usePlatformStore from "@store/usePlatformStore";
import meetingService from "@services/meeting.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { ActiveScreen } from "@enums/screens.enum";
import {
  MEETING_QUERY_KEY,
  OBJECT_TAGS_QUERY_KEY,
  REMOVE_MEETING_MUTATION_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
} from "@config/query";

export function useRemoveMeeting(onSuccess?: () => void) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { itemId } = usePlatformStore();
  const { changeActiveScreen } = useSystemStore();
  const { meetingId, setMeetingId } = useMeetingStore();

  return useMutation({
    mutationKey: [REMOVE_MEETING_MUTATION_KEY, meetingId],
    mutationFn: async (relationsIds: string[]) => {
      return await meetingService.removeMeeting(meetingId, relationsIds);
    },
    onSuccess: (response) => {
      setMeetingId(null);
      changeActiveScreen(ActiveScreen.SYNC_MEETING);

      // NOTE: not calling removeQueries for MEETING_QUERY_KEY to avoid immediate refetching of the meeting
      // (which caused unwanted changing back to the Meeting screen)
      queryClient.setQueryData([MEETING_QUERY_KEY, itemId], null);
      queryClient.removeQueries({ queryKey: [OBJECT_TAGS_QUERY_KEY] });
      queryClient.removeQueries({ queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY] });

      ToastService.showSuccess("Meeting Successfully Removed");

      onSuccess?.();
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      notifyBugsnag(error, ERROR_STRINGS.REMOVE_MEETING);
    },
  });
}
